import React, { useState, useEffect, useRef  } from 'react'
import Modal from './book/steps/components/Modal'
import { useResponsiveContext } from '../components/ResponsiveContext'
import { Text, Header2 } from '../styles/Components'
import Button from './Button'
import { gsColors } from '../styles/Variables'
import styled from '@emotion/styled'
import {
  mq,
  resendCooldownKey,
  useResendCooldown,
  getResendCooldownRemaining,
  clearResendCooldown,
  normalizePhoneNumber,
} from '../helpers'

const OTPVerificationContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem 1rem 1rem;

  ${mq['md']} {
    padding: 0 1rem 1.5rem 1rem;
  }

  h2 {
    text-align: center;
    margin-bottom: 12px;
    font-size: 22px;

    ${mq['md']} {
      font-size: 32px;
      margin-bottom: 24px;
    }
  }

  .otp-inputs-container {
    display: flex;
    justify-content: center;
    gap: 5px;
    margin: 12px 0 8px;
    position: relative;

    ${mq['md']} {
      gap: 8px;
      margin: 24px 0 16px;
    }

    /*
     * The field that actually holds the code — see the matching note in
     * sign-up-sign-in-form/otp-input.js. It covers the boxes at full size and is hidden with
     * transparent text rather than a height of 0, because iOS Safari only offers the "From
     * Messages" suggestion for a field it considers rendered.
     */
    .otp-real-input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      border: none;
      background-color: transparent;
      color: transparent;
      caret-color: transparent;
      text-align: center;
      pointer-events: none;
      /* keep >= 16px: iOS zooms the page when a smaller-text field takes focus */
      font-size: 16px;
      font-family: Euclid;

      &:focus {
        outline: none;
      }
    }

    .otp-input-field {
      width: 36px;
      height: 46px;
      border: 1px solid #ababab;
      text-align: center;
      font-size: 18px;
      font-family: Euclid;
      border-radius: 3px;
      cursor: pointer;

      ${mq['md']} {
        width: 46px;
        height: 55px;
        font-size: 20px;
      }
    }

    .otp-input-field.focused {
      border-color: ${gsColors.cobalt};
      outline: none;
      box-shadow: 0 0 0 2px rgba(0, 90, 158, 0.25);
    }
  }

  .button-container {
    margin-top: 12px;
    display: flex;
    gap: 12px;
    width: 100%;
    justify-content: center;

    ${mq['md']} {
      margin-top: 24px;
    }
  }

  .resend-container {
    margin-top: 12px;

    ${mq['md']} {
      margin-top: 24px;
    }

    button {
      background-color: transparent;
      border: none;
      color: ${gsColors.sunsetOrange};
      font-family: Euclid;
      font-size: 16px;
      font-weight: 400;
      cursor: pointer;
      text-decoration: underline;

      &:disabled {
        color: ${gsColors.darkGrey};
        cursor: default;
      }
    }
  }
`

const OTPVerificationModal = ({
  isOpen,
  closeModal,
  phoneNumber,
  cloudflareTokenRef,
  onVerificationSuccess,
}) => {
  const { isDesktop } = useResponsiveContext()  
  const OTP_LENGTH = 6
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''))
  const [activeIdx, setActiveIdx] = useState(0)
  const [error, setError] = useState('')
  const [working, setWorking] = useState(false)
  /*
   * phoneNumber arrives as free text from the lead form, so "2125550123", "12125550123" and
   * "+12125550123" would otherwise key three separate cooldowns for the same handset — a
   * retyped number would bypass the throttle entirely.
   * Normalize once here and derive the cooldown key from that. Fall back to the raw value
   * when it doesn't parse, so an unparseable number still gets *a* key instead of a falsy one
   * that would silently disable the throttle.
   */
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber) || phoneNumber
  const cooldownKey = resendCooldownKey('smsVerify', normalizedPhoneNumber)
  const { secondsRemaining, isCoolingDown, startCooldown } = useResendCooldown(cooldownKey)
  const hiddenInputRef = useRef(null)

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '')
    const characters = value.split('')
    const filler = Array(OTP_LENGTH - characters.length).fill('')

    setOtp([...characters, ...filler])
    setActiveIdx(value.length)
    setError('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace') {
      if (hiddenInputRef.current.value.length > 0) {
        const newOtp = otp.slice()
        newOtp[activeIdx] = ''
        setOtp(newOtp)
        setActiveIdx(activeIdx > 0 ? activeIdx - 1 : 0)
      }
    }
  }

  const handleClick = (index) => {
    hiddenInputRef.current.focus()
    hiddenInputRef.current.setSelectionRange(index, index)
    setActiveIdx(index)
  }

  const sendOTP = async () => {
    setWorking(true)
    setError('')
    try {
      const response = await fetch('/api/verify/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber,
          cloudflareToken: cloudflareTokenRef?.current
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification code')
      }

      startCooldown()
    } catch (e) {
      console.error(e)
      setError(e.message || 'Failed to send verification code')
    } finally {
      setWorking(false)
    }
  }

  const verifyOTP = async () => {
    const code = otp.join('')

    if (code.length !== OTP_LENGTH) {
      setError('Please enter a complete verification code')
      return
    }

    setWorking(true)
    setError('')

    try {
      // Twilio checks the code against the phone number (no verification id needed), and
      // phoneNumber is what the server binds the signed proof token to.
      const response = await fetch('/api/verify/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, phoneNumber }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code')
      }

      // the code is spent the moment verification succeeds — clear the cooldown so a reload
      // that reopens this modal doesn't sit behind a countdown for a code that can no longer
      // be exchanged
      clearResendCooldown(cooldownKey)

      // hand the signed proof token to the form; it travels to /api/events, which
      // re-verifies it server-side as the spam gate
      onVerificationSuccess({ verifyToken: data.verifyToken })
      closeModal()
    } catch (e) {
      console.error(e)
      setError(e.message || 'Invalid verification code')
    } finally {
      setWorking(false)
    }
  }

  const resendCode = async () => {
    setOtp(Array(OTP_LENGTH).fill(''))
    setActiveIdx(0)
    setError('')
    await sendOTP()
  }

  /*
   * Auto-send on open, unless a code for this number went out less than a minute ago —
   * otherwise closing and reopening the modal is a free SMS. A skipped send is safe because
   * Twilio checks the code by phone number, so the code already delivered still verifies.
   */
  useEffect(() => {
    if (!isOpen || !phoneNumber) {
      return
    }

    hiddenInputRef.current?.focus()

    if (getResendCooldownRemaining(cooldownKey) > 0) {
      return
    }

    sendOTP()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, phoneNumber])

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      width='500px'
      isDesktop={isDesktop}
    >
      <OTPVerificationContainer>
        <Header2>Verify Phone Number</Header2>
        <Text textAlign="center" margin="0 0 12px" fontSize="14px" style={{ lineHeight: '1.4' }}>
          Please enter the verification code sent to {phoneNumber}.
        </Text>

        <div className='otp-inputs-container'>
          {/* the real field, layered over the boxes — see .otp-real-input above */}
          <input
            ref={hiddenInputRef}
            id='verification-code'
            type="text"
            name="one-time-code"
            aria-label='Verification code'
            value={otp.join('')}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            maxLength={OTP_LENGTH}
            className='otp-real-input'
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />
          {/* presentational duplicates of the real field: hidden from assistive tech,
              skipped by tab, and never a target for autofill */}
          {otp.map((value, idx) => (
            <input
              key={idx}
              type='text'
              value={value}
              readOnly
              aria-hidden='true'
              tabIndex={-1}
              autoComplete='off'
              onClick={() => handleClick(idx)}
              className={`otp-input-field ${idx === activeIdx ? 'focused' : ''}`}
              maxLength={1}
            />
          ))}
        </div>

        {error && (
          <Text margin='1rem 0 0' variant='small' color={gsColors.errorRed}>
            {error}
          </Text>
        )}

        <div className="button-container">
          <Button
            onClick={verifyOTP}
            working={working}
            color={gsColors.cobalt}
            disabled={otp.join('').length !== OTP_LENGTH}
          >
            Verify
          </Button>
        </div>

        <div className="resend-container">
          <button
            type='button'
            onClick={resendCode}
            disabled={working || isCoolingDown}
          >
            {isCoolingDown ? `Resend code in ${secondsRemaining}s` : 'Resend code'}
          </button>
        </div>
      </OTPVerificationContainer>
    </Modal>
  )
}

export default OTPVerificationModal
